import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';

import { getBackendUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  const validateEmail = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (!EMAIL_REGEX.test(trimmed)) return 'Adresse email invalide';
    return '';
  };

  // 🎯 Iter381 — Mapping premium des erreurs HTTP du backend `/api/auth/forgot-password`.
  // Le `safeFetch` historique transformait toute erreur (y compris 404 EMAIL_NOT_FOUND)
  // en exception network → message générique "Erreur de connexion au serveur".
  // Désormais on utilise `fetch` natif et on parse précisément le statut + `detail` JSON.
  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setLoading(true);
    try {
      let response;
      try {
        response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim().toLowerCase() }),
        });
      } catch (networkErr) {
        // 🌐 Vraie erreur réseau (offline, DNS, CORS, etc.) — distinguée des erreurs HTTP.
        console.error('[forgot-password] network error:', networkErr);
        toast.error('🌐 Connexion réseau indisponible. Vérifie ton accès internet et réessaye.');
        setLoading(false);
        return;
      }

      // Parse safe du JSON (certaines erreurs 502/504 retournent du HTML/text)
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (response.ok) {
        setEmailSent(true);
        toast.success('✅ Un email de réinitialisation vient de t\'être envoyé.');
        return;
      }

      // Mapping erreurs HTTP → messages premium FR
      const detail = (data && (data.detail || data.message)) || '';

      if (response.status === 404 || detail === 'EMAIL_NOT_FOUND') {
        toast.error('❌ Aucun compte n\'est associé à cette adresse email.');
        setEmailError('Aucun compte n\'est associé à cette adresse email.');
      } else if (response.status === 429 || detail === 'TOO_MANY_REQUESTS') {
        toast.error('⚠️ Trop de tentatives. Réessaye dans 15 minutes.');
      } else if (response.status === 422 || response.status === 400) {
        toast.error('❌ Adresse email invalide. Vérifie le format.');
        setEmailError('Adresse email invalide.');
      } else if (response.status >= 500) {
        toast.error('🛠️ Service temporairement indisponible. Réessaye dans quelques minutes.');
      } else {
        toast.error('❌ Une erreur est survenue. Réessaye plus tard.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="w-full max-w-md">
        {/* Bouton retour */}
        <button
          onClick={() => navigate('/login')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </button>

        <Card className="shadow-xl border-2">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-2">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Mot de passe oublié ?
            </CardTitle>
            <CardDescription>
              {emailSent
                ? 'Email envoyé ! Consulte ta boîte de réception.'
                : 'Entre ton adresse email pour recevoir un lien de réinitialisation.'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {emailSent ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-800">
                    ✅ Un email de réinitialisation vient de t'être envoyé à :
                  </p>
                  <p className="font-semibold text-green-900 mt-1">{email}</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs text-blue-800">
                    💡 <strong>N'oublie pas de vérifier :</strong>
                  </p>
                  <ul className="text-xs text-blue-700 mt-2 space-y-1 list-disc list-inside">
                    <li>Ton dossier spam/courrier indésirable</li>
                    <li>Le lien expire dans 24 heures</li>
                  </ul>
                </div>

                <div className="pt-4 space-y-2">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full"
                    variant="outline"
                  >
                    Retour à la connexion
                  </Button>
                  
                  <button
                    onClick={() => {
                      setEmailSent(false);
                      setEmail('');
                    }}
                    className="w-full text-xs text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Renvoyer un email
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Adresse email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ton.email@exemple.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError(validateEmail(e.target.value));
                    }}
                    onBlur={(e) => setEmailError(validateEmail(e.target.value))}
                    required
                    autoFocus
                    className={emailError ? 'border-red-400 focus:ring-red-400' : ''}
                  />
                  {emailError && (
                    <p className="text-xs text-red-500">{emailError}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  disabled={loading}
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
                </Button>

                <p className="text-xs text-center text-gray-500 mt-4">
                  Tu te souviens de ton mot de passe ?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    Connecte-toi
                  </button>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
